import { clientConfig } from '@/config/clientConfig';
import { useToast } from '@/shared/hooks/useToast';
import { normalizeEmail } from '@/shared/lib/email-utils';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { AlertCircle, Eye, EyeOff, Loader2, LogIn, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuthStore } from '../store/authStore';

export default function LoginPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login, ssoLogin, isAuthenticated, isLoading, error, clearError } = useAuthStore();
  const [ssoRedirecting, setSsoRedirecting] = useState(false);
  const sso = clientConfig.sso;

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  const validateForm = (): boolean => {
    if (!username.trim()) {
      setValidationError('Email address is required');
      return false;
    }

    if (!password) {
      setValidationError('Password is required');
      return false;
    }

    if (password.length < 6) {
      setValidationError('Password must be at least 6 characters');
      return false;
    }

    setValidationError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Normalize email to lowercase to handle Cognito's case-sensitive usernames
      await login({ username: normalizeEmail(username), password });
      const { pendingChallenge: challenge } = useAuthStore.getState();
      if (challenge) {
        navigate('/change-password', { replace: true });
        return;
      }
      toast({
        title: 'Login Successful',
        description: 'Welcome back!',
        className:
          'bg-green-50 border-green-200 text-green-900 dark:bg-green-900 dark:text-green-100',
      });
      navigate('/', { replace: true });
    } catch (_err) {
      toast({
        title: 'Login Failed',
        description: error || 'Invalid email or password. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleSsoLogin = async () => {
    setSsoRedirecting(true);
    try {
      // Redirects the browser to Cognito on success; no further action here.
      await ssoLogin(sso.identityProvider);
    } catch (_err) {
      setSsoRedirecting(false);
      toast({
        title: 'SSO Unavailable',
        description: error || 'Unable to start single sign-on. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const displayError = validationError || error;
  const ssoLabel = sso.buttonLabel || clientConfig.name;

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `url(${clientConfig.loginBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Overlay for better text contrast */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/40 to-green-900/40" />

      <Card className="w-full max-w-md p-8 space-y-6 bg-white dark:bg-gray-900 shadow-2xl border-border relative z-10">
        {/* Logo and Title */}
        <div className="text-center space-y-2">
          <div className="flex justify-center mb-4">
            <img src={clientConfig.logo} alt={clientConfig.name} className="h-12 w-auto" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Welcome to {clientConfig.name}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Sign in to access your field service management dashboard
          </p>
        </div>

        {/* SSO Banner — shown when the client has single sign-on enabled */}
        {sso.enabled && (
          <div className="space-y-4">
            <Button
              type="button"
              onClick={handleSsoLogin}
              disabled={isLoading || ssoRedirecting}
              className="w-full text-white"
              style={{ backgroundColor: clientConfig.theme.primary }}
              size="lg"
            >
              {ssoRedirecting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Redirecting...
                </>
              ) : (
                <>
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  Log in with {ssoLabel}
                </>
              )}
            </Button>

            {/* Divider between SSO and email/password */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
              <span className="text-xs text-gray-500 dark:text-gray-400">or</span>
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
            </div>
          </div>
        )}

        {/* Error Alert */}
        {displayError && (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800 dark:text-red-200">
                Authentication Error
              </p>
              <p className="text-sm text-red-700 dark:text-red-300 mt-0.5">{displayError}</p>
            </div>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email Field */}
          <div className="space-y-2">
            <Label
              htmlFor="username"
              className="text-sm font-medium text-gray-900 dark:text-gray-100"
            >
              Email Address
            </Label>
            <Input
              id="username"
              type="email"
              placeholder="your.email@company.com"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                setValidationError(null);
                clearError();
              }}
              disabled={isLoading}
              className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100"
              autoComplete="email"
              required
            />
          </div>

          {/* Password Field */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label
                htmlFor="password"
                className="text-sm font-medium text-gray-900 dark:text-gray-100"
              >
                Password
              </Label>
              <button
                type="button"
                className="text-xs hover:underline font-medium text-gray-900 dark:text-white"
                onClick={() => {
                  const trimmed = username.trim();
                  navigate(
                    trimmed
                      ? `/forgot-password?email=${encodeURIComponent(trimmed)}`
                      : '/forgot-password',
                  );
                }}
              >
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setValidationError(null);
                  clearError();
                }}
                disabled={isLoading}
                className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 pr-10"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isLoading}
            className="w-full text-white"
            style={{ backgroundColor: clientConfig.theme.primary }}
            size="lg"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Signing in...
              </>
            ) : (
              <>
                <LogIn className="w-4 h-4 mr-2" />
                Sign In
              </>
            )}
          </Button>
        </form>

        {/* Footer */}
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-center text-gray-500 dark:text-gray-400">
            By signing in, you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>
      </Card>
    </div>
  );
}
